import { useState } from "react"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, Download } from "lucide-react"
import { toast } from "sonner"
import type { ContactImport } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Header, Hint, Stats, Status } from "@/components/audience/primitives"
import { downloadText, formatCount } from "@/lib/download"
import { api, ApiError } from "@/lib/api"
import { importQuery, importRowsQuery, workspaceQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/contacts/import/$importId/review")({
  beforeLoad: async ({ context, params }) => {
    const ctx = await context.queryClient.ensureQueryData(workspaceQuery(params.workspace))
    if (!ctx.permissions.includes("manage_customer_data")) throw redirect({ to: "/w/$workspace/contacts", params, search: {} })
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(importQuery(params.workspace, params.importId)),
  component: ImportReview,
})

const DECISION_LABELS = { create: "Create profile", update: "Update · reviewed", skip: "Skip match", error: "Skip row" } as const

/** Screen 33. */
function ImportReview() {
  const { workspace } = useWorkspaceContext()
  const { importId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const imported = useSuspenseQuery(importQuery(workspace.slug, importId)).data
  const [tab, setTab] = useState("all")
  const [confirm, setConfirm] = useState(false)
  const rows = useQuery({ ...importRowsQuery(workspace.slug, importId, tab === "all" ? undefined : tab), placeholderData: (previous) => previous })
  const params = { workspace: workspace.slug }
  const summary = imported.summary ?? { create: 0, update: 0, skip: 0, error: 0 }
  const valid = summary.create + summary.update
  const done = imported.status === "completed"

  const commit = useMutation({
    mutationFn: () => api<ContactImport>(`/w/${workspace.slug}/contacts/imports/${importId}/commit`, { method: "POST" }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "contacts"] })
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "tags"] })
      setConfirm(false)
      toast.success(`${(result.summary?.create ?? 0) + (result.summary?.update ?? 0)} contacts imported`)
      await navigate({ to: "/w/$workspace/contacts", params, search: {} })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "The import could not run"),
  })

  async function downloadErrors() {
    const response = await fetch(`/api/w/${workspace.slug}/contacts/imports/${importId}/errors.csv`, { credentials: "same-origin" })
    downloadText("contact-import-errors.csv", await response.text(), "text/csv;charset=utf-8;")
  }

  return (
    <div className="page-wrap au-root">
      <Header title={done ? "Import complete" : "Review your import"} description={`${imported.fileName} · ${formatCount(imported.rowCount)} rows`} back={<Link to="/w/$workspace/contacts/import/$importId" params={{ ...params, importId }}><ArrowLeft /> Map columns</Link>}>
        <Button variant="outline" onClick={() => void downloadErrors()} disabled={summary.error === 0}>
          <Download /> Download errors
        </Button>
      </Header>
      <Stats
        items={[
          { label: "Rows in file", value: formatCount(imported.rowCount), detail: `${Object.values(imported.mapping).filter((t) => t !== "ignore").length} mapped columns` },
          { label: "Create", value: formatCount(summary.create), detail: "New person profiles" },
          { label: imported.options.reviewMatches ? "Update" : "Matches skipped", value: formatCount(imported.options.reviewMatches ? summary.update : summary.skip), detail: imported.options.reviewMatches ? "Reviewed exact email matches" : "Updates disabled in mapping" },
          { label: "Errors", value: formatCount(summary.error), detail: "Skipped until corrected" },
        ]}
      />
      <Tabs value={tab} onValueChange={setTab} className="mt-7">
        <TabsList variant="line" className="au-tab-line">
          <TabsTrigger value="all">All rows</TabsTrigger>
          <TabsTrigger value="create">New · {summary.create}</TabsTrigger>
          <TabsTrigger value={imported.options.reviewMatches ? "update" : "skip"}>Matches · {imported.options.reviewMatches ? summary.update : summary.skip}</TabsTrigger>
          <TabsTrigger value="error">Errors · {summary.error}</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="au-table mt-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Row</TableHead>
              <TableHead>Imported person</TableHead>
              <TableHead>Match / validation</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead>Fields changed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(rows.data?.rows ?? []).map((row) => (
              <TableRow key={row.rowNumber}>
                <TableCell className="text-muted-foreground">{row.rowNumber}</TableCell>
                <TableCell>
                  <p className="font-medium">{row.name}</p>
                  <p className="text-xs text-muted-foreground">{row.email ?? "No email"}</p>
                </TableCell>
                <TableCell>{row.match}</TableCell>
                <TableCell>
                  <Status>{DECISION_LABELS[row.decision]}</Status>
                </TableCell>
                <TableCell className="text-muted-foreground">{row.changes.join(", ") || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Showing {rows.data?.rows.length ?? 0} of {formatCount(rows.data?.total ?? 0)} rows.
      </p>
      <div className="mt-6">
        <Hint>{done ? "This import has run. Existing permissions and identity links stayed intact." : `${formatCount(valid)} valid rows will be imported. Existing permissions and identity links stay intact. Similar names never merge people.`}</Hint>
      </div>
      <footer className="au-footer">
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/contacts/import/$importId" params={{ ...params, importId }}>
            <ArrowLeft /> Back
          </Link>
        </Button>
        <span className="mr-auto text-xs text-muted-foreground">{done ? `Completed · ${summary.create} created, ${summary.update} updated` : "Nothing changes until you confirm."}</span>
        {done ? (
          <Button asChild>
            <Link to="/w/$workspace/contacts" params={params} search={{}}>
              View contacts
              <ArrowRight />
            </Link>
          </Button>
        ) : (
          <Button disabled={valid === 0} onClick={() => setConfirm(true)}>
            Import {formatCount(valid)} valid rows
            <ArrowRight />
          </Button>
        )}
      </footer>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import {formatCount(valid)} valid rows?</DialogTitle>
            <DialogDescription>
              {summary.create} new profiles{imported.options.reviewMatches && summary.update ? ` and ${summary.update} reviewed updates` : ""}. {summary.error ? `${summary.error} invalid rows are skipped. ` : ""}No marketing permission or channel identity is created.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>
              Review again
            </Button>
            <Button disabled={commit.isPending} onClick={() => commit.mutate()}>
              {commit.isPending ? "Importing…" : "Confirm import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
